import {
  DeviceCreateRequest,
  DeviceOperationRequest,
  DeviceSearchRequest,
  DeviceSettingForm,
  DeviceSettingRequest,
  TerminalEditForm,
  TerminalImportRequest,
  TrafficDataRequest,
  DeviceDetailRequest,
} from "./TerminalForm";
import axios from "axios";
import { AxiosResponse } from "axios";
import { Device } from "../../../entity/Device";
import { PullDownItem } from "../../common/form/FormPullDown";
import { DeviceUser } from "../../../entity/DeviceUser";
import { ApplicationApi } from "../../../Routing";

/**
 * 端末管理 API
 */
export class TerminalService {
  // 端末一覧取得API
  findByConditions = async (
    param: DeviceSearchRequest
  ): Promise<AxiosResponse> => {
    try {
      const res: AxiosResponse = await axios.post(
        ApplicationApi.deviceSearch.apiUrl(),
        param
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末詳細取得API
  findById = async (imei: string): Promise<AxiosResponse> => {
    try {
      const res: AxiosResponse = await axios.get(
        ApplicationApi.deviceDetail.apiUrl(imei),
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末詳細取得API（SIM差し替えログ版）
  findDetailByParams = async (request: DeviceDetailRequest): Promise<AxiosResponse> => {
    try {
      const res: AxiosResponse = await axios.post(
        ApplicationApi.deviceDetail.apiUrl(request.id),
        request
      );
      return res;
    } catch (err) {
      throw err;
    }
  }

  // 端末情報編集確認API
  updateConfirm = async (param: TerminalEditForm): Promise<AxiosResponse> => {
    try {
      let entity = new Device();
      entity.imei = param.imei;
      entity.tenantId = param.tenantId;
      entity.deviceName = param.deviceName;
      entity.deviceUserId = param.deviceUserId;
      entity.isMdmManaged = param.isMdmManaged;
      entity.comment = param.comment;
      entity.commentMode = param.commentMode;
      let tagId: string[] = [];
      param.selectTag.forEach((item) => {
        tagId.push(item.key);
      });
      entity.tagId = tagId;
      entity.tagMode = param.tagMode;

      let request = new DeviceCreateRequest();
      request.editdata = entity;
      const res: AxiosResponse = await axios.post(
        "mdm_web/mdmDevice/update/confirm",
        request
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末情報編集API
  update = async (param: TerminalEditForm): Promise<AxiosResponse> => {
    try {
      let entity = new Device();
      entity.imei = param.imei;
      entity.tenantId = param.tenantId;
      entity.deviceName = param.deviceName;
      entity.deviceUserId = param.deviceUserId;
      entity.isMdmManaged = param.isMdmManaged;
      entity.comment = param.comment;
      entity.commentMode = param.commentMode;
      let tagId: string[] = [];
      param.selectTag.forEach((item) => {
        tagId.push(item.key);
      });
      entity.tagId = tagId;
      entity.tagMode = param.tagMode;

      let request = new DeviceCreateRequest();
      request.editdata = entity;
      const res: AxiosResponse = await axios.post(
        "mdm_web/mdmDevice/update",
        request
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 利用者選択肢取得API
  getDeviceUserOption = async () => {
    const option: PullDownItem[] = [];
    try {
      const result: AxiosResponse = await axios.post(
        "/mdm_web/mdmDevice/selectDeviceUser",
        {}
      );
      const deviceUser = result.data.result.deviceUser as DeviceUser;
      Object.entries(deviceUser).forEach(([index, deviceUser]) => {
        let item: PullDownItem = {
          key: deviceUser.id,
          value: deviceUser.username,
        };
        option.push(item);
      });
    } catch (err) {}

    return option;
  };

  // 端末情報編集取得API
  findByEditId = async (deviceId: string[]): Promise<AxiosResponse> => {
    try {
      const res: AxiosResponse = await axios.post(
        ApplicationApi.deviceEdit.apiUrl(),
        {deviceId,}
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末情報削除API
  delete = async (deviceId: string[]): Promise<AxiosResponse> => {
    try {
      const res: AxiosResponse = await axios.post(
        ApplicationApi.deviceDelete.apiUrl(),
        {deviceId,}
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末インポート確認API
  importConfirm = async (uploadFile: FormData): Promise<AxiosResponse> => {
    try {
      const res: AxiosResponse = await axios.post(
        ApplicationApi.deviceImportConfirm.apiUrl(),
        uploadFile
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末インポートAPI
  import = async (param: TerminalImportRequest): Promise<AxiosResponse> => {
    try {
      const res: AxiosResponse = await axios.post(
        ApplicationApi.deviceImport.apiUrl(),
        param
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末情報エクスポートAPI
  export = async (param: DeviceSearchRequest): Promise<AxiosResponse> => {
    try {
      const res: AxiosResponse = await axios.post(
        "/mdm_web/mdmDevice/export",
        param,
        { responseType: "blob" }
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末設定取得API
  loadSetting = async (deviceId: string): Promise<AxiosResponse> => {
    try {
      const res: AxiosResponse = await axios.get(
        `mdm_web/mdmDevice/${deviceId}/loadSetting`
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末設定送信API
  saveSetting = async (
    id: string[],
    items: (keyof DeviceSettingForm)[],
    param: Partial<DeviceSettingForm>
  ): Promise<AxiosResponse> => {
    try {
      const savedata: Partial<Record<keyof DeviceSettingForm, any>> = {};
      items.forEach((item) => {
        savedata[item] = param[item];
      });
      const request = new DeviceSettingRequest(id, savedata);
      const res: AxiosResponse = await axios.post(
        "/mdm_web/mdmDevice/saveSetting",
        request
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 端末操作API
  operate = async (param: DeviceOperationRequest) => {
    try {
      const res: AxiosResponse = await axios.post(
        "/mdm_web/mdmDevice/operate",
        param
      );
      return res;
    } catch (err) {
      throw err;
    }
  };

  // 通信量取得API
  getTraffic = async (param: TrafficDataRequest) => {
    try {
      const res: AxiosResponse = await axios.post(
        "mdm_web/mdmDevice/getTraffic",
        param
      );
      return res;
    } catch (err) {
      throw err;
    }
  };
}
